import { AppleLogo, LinuxLogo, WindowsLogo, GithubLogo, XLogo, DiscordLogo, Heart, ArrowsClockwise, X, UsersThree, ShareNetwork } from "@phosphor-icons/react";
import { Fingerprint, Boxes, Network, Gem, Layers } from "lucide-react";
import { motion, useMotionValue, useSpring, useTransform, useScroll, useMotionValueEvent, AnimatePresence } from "motion/react";
import {
	Suspense,
	lazy,
	useState,
	useCallback,
	useEffect,
	useRef,
	useMemo,
} from "react";
import logoLockup from "@/assets/images/basilica-logo-lockup.svg";
import { BasilicaLogo } from "@/components/basilica-logo";
import { cn } from "@/lib/utils";
import { GlobeSpinner } from "./globe-spinner";

// Hook to detect scroll direction
function useScrollDirection(containerRef: React.RefObject<HTMLDivElement | null>) {
	const [hidden, setHidden] = useState(false);
	const { scrollY } = useScroll({ container: containerRef });
	const lastScrollY = useRef(0);

	useMotionValueEvent(scrollY, "change", (latest) => {
		const previous = lastScrollY.current;
		const direction = latest > previous ? "down" : "up";

		// Only hide after scrolling down past 100px
		if (direction === "down" && latest > 100) {
			setHidden(true);
		} else if (direction === "up") {
			setHidden(false);
		}

		lastScrollY.current = latest;
	});

	return hidden;
}
// Ambient mouse-following glow
function AmbientGlow() {
	const mouseX = useMotionValue(0);
	const mouseY = useMotionValue(0);

	const smoothX = useSpring(mouseX, { damping: 50, stiffness: 100 });
	const smoothY = useSpring(mouseY, { damping: 50, stiffness: 100 });

	useEffect(() => {
		const handleMouseMove = (e: MouseEvent) => {
			mouseX.set(e.clientX);
			mouseY.set(e.clientY);
		};

		window.addEventListener("mousemove", handleMouseMove, { passive: true });
		return () => window.removeEventListener("mousemove", handleMouseMove);
	}, [mouseX, mouseY]);

	return (
		<motion.div
			className="pointer-events-none fixed inset-0 z-0"
			style={{
				background: useTransform(
					[smoothX, smoothY],
					([x, y]) =>
						`radial-gradient(800px circle at ${x}px ${y}px, rgba(180, 140, 80, 0.04), transparent 60%)`
				),
			}}
		/>
	);
}

// Lazy load Earth component
const Earth = lazy(() => import("./earth"));

// Special characters for the scramble effect
const scrambleChars = "#@$%&*!?+=-~^<>|/\\[]{}:;";

// Custom easing function - exponential ease out for dramatic slow down
const easeOutExpo = (t: number): number => {
	return t === 1 ? 1 : 1 - 2 ** (-10 * t);
};

// Individual character with scramble animation
function ScrambleChar({
	char,
	isSpace,
	initialDelay = 0,
}: {
	char: string;
	isSpace: boolean;
	initialDelay?: number;
}) {
	const [displayChar, setDisplayChar] = useState(char);
	const [opacity, setOpacity] = useState(1);
	const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const isAnimatingRef = useRef(false);

	const runScramble = useCallback((duration: number = 800) => {
		if (isSpace || char === "," || char === ".") return;
		if (isAnimatingRef.current) return;

		isAnimatingRef.current = true;
		const startTime = performance.now();
		const totalIterations = 12;
		let iteration = 0;

		const scheduleNext = () => {
			const elapsed = performance.now() - startTime;
			const progress = Math.min(elapsed / duration, 1);
			const eased = easeOutExpo(progress);

			const minDelay = 15;
			const maxDelay = 180;
			const delay = minDelay + (maxDelay - minDelay) * eased;

			setOpacity(0.7 + 0.3 * (0.5 + 0.5 * Math.sin(iteration * 0.8)));

			if (iteration < totalIterations && progress < 0.95) {
				const shouldSettle = Math.random() < eased * eased;
				if (shouldSettle && progress > 0.3) {
					setDisplayChar(char);
				} else {
					const randomChar = scrambleChars[Math.floor(Math.random() * scrambleChars.length)];
					setDisplayChar(randomChar);
				}
				iteration++;
				timeoutRef.current = setTimeout(scheduleNext, delay);
			} else {
				setDisplayChar(char);
				setOpacity(1);
				isAnimatingRef.current = false;
			}
		};

		setDisplayChar(scrambleChars[Math.floor(Math.random() * scrambleChars.length)]);
		timeoutRef.current = setTimeout(scheduleNext, 15);
	}, [char, isSpace]);

	// Initial cascade animation on mount
	useEffect(() => {
		if (isSpace || char === "," || char === ".") return;

		const timer = setTimeout(() => {
			runScramble(700);
		}, initialDelay);

		return () => clearTimeout(timer);
	}, [char, initialDelay, isSpace, runScramble]);

	useEffect(() => {
		return () => {
			if (timeoutRef.current) {
				clearTimeout(timeoutRef.current);
			}
		};
	}, []);

	if (isSpace) {
		return <span aria-hidden="true"> </span>;
	}

	return (
		<span
			aria-hidden="true"
			className="transition-opacity duration-75"
			style={{ opacity }}
		>
			{displayChar}
		</span>
	);
}

// Hover scramble text component
function ScrambleText({
	text,
	className,
	cascadeOnMount = true,
}: {
	text: string;
	className?: string;
	cascadeOnMount?: boolean;
}) {
	const chars = useMemo(
		() => text.split("").map((char, index) => ({ char, index, id: `${char}-${index}` })),
		[text]
	);

	return (
		<span className={cn("cursor-default", className)}>
			<span className="sr-only">{text}</span>
			{chars.map(({ char, index, id }) => (
				<ScrambleChar
					key={id}
					char={char}
					isSpace={char === " "}
					initialDelay={cascadeOnMount ? 400 + index * 35 : 0}
				/>
			))}
		</span>
	);
}

// Stat pill component - bordered pill for technical terms
function StatPill({ children }: { children: React.ReactNode }) {
	return (
		<span className="inline-flex items-center px-4 py-2 rounded-full border border-white/20 text-sm text-white/70 font-mono">
			{children}
		</span>
	);
}

// Dotted divider component
function DottedDivider() {
	return (
		<div className="w-full border-b border-dotted border-white/15" />
	);
}

// AI model logo with graceful text fallback
function ModelLogo({ name, slug }: { name: string; slug: string }) {
	const [failed, setFailed] = useState(false);
	if (failed) {
		return <span className="text-[15px] font-semibold text-white/25 hover:text-white transition-colors duration-300 select-none">{name}</span>;
	}
	return (
		<img
			src={`https://cdn.simpleicons.org/${slug}`}
			alt={name}
			title={name}
			className="h-5 opacity-30 grayscale hover:opacity-100 hover:grayscale-0 transition-all duration-300"
			loading="lazy"
			onError={() => setFailed(true)}
		/>
	);
}

// Press logo with text fallback
function PressLogo({ name, src }: { name: string; src: string }) {
	const [failed, setFailed] = useState(false);
	if (failed) {
		return <span className="text-[15px] font-semibold tracking-wide text-white/30 select-none uppercase">{name}</span>;
	}
	return (
		<img
			src={src}
			alt={name}
			className="h-5 brightness-0 invert opacity-40"
			loading="lazy"
			onError={() => setFailed(true)}
		/>
	);
}

const AI_MODELS = [
	{ name: "Claude", slug: "claude" },
	{ name: "Codex", slug: "openai" },
	{ name: "Gemini", slug: "googlegemini" },
	{ name: "Grok", slug: "xai" },
	{ name: "DeepSeek", slug: "deepseek" },
	{ name: "Kimi", slug: "kimi" },
];

// Unified button component
function Button({
	icon: Icon,
	children,
	variant = "default",
	size = "default",
	type = "button",
	disabled = false,
	onClick,
	className,
}: {
	icon?: React.ComponentType<{ size?: number; weight?: "fill" | "regular" | "bold" }>;
	children: React.ReactNode;
	variant?: "default" | "gold" | "solid";
	size?: "default" | "large";
	type?: "button" | "submit";
	disabled?: boolean;
	onClick?: () => void;
	className?: string;
}) {
	const hasMotionHover = variant !== "default";

	return (
		<motion.button
			type={type}
			disabled={disabled}
			onClick={onClick}
			whileHover={disabled || !hasMotionHover ? {} : { scale: 1.03, y: -2 }}
			whileTap={disabled || !hasMotionHover ? {} : { scale: 0.97 }}
			transition={{ type: "spring", stiffness: 400, damping: 17 }}
			className={cn(
				"group relative flex items-center justify-center gap-2.5 rounded-xl",
				"transition-all duration-100",
				"focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/30",
				size === "large" ? "px-6 py-3.5 h-[48px]" : "px-4 py-2 h-[40px]",
				disabled && "cursor-not-allowed",
				variant === "gold" && !disabled && "bg-gold/15 border border-gold/30 text-gold hover:bg-gold/25 hover:border-gold/50 hover:shadow-[0_8px_32px_-8px_rgba(180,140,80,0.3)]",
				variant === "default" && !disabled && "bg-white/10 hover:bg-white/90",
				variant === "default" && disabled && "bg-white/10",
				variant === "solid" && !disabled && "bg-white text-[#0a0a0a] hover:bg-white/90 hover:shadow-[0_8px_32px_-8px_rgba(255,255,255,0.25)]",
				variant === "solid" && disabled && "bg-white/10",
				className
			)}
		>
			{Icon && (
				<span className={cn(
					"transition-colors duration-100",
					disabled ? "text-white/40" : variant === "gold" ? "text-gold" : variant === "solid" ? "text-[#0a0a0a]" : "text-white group-hover:text-[#0a0a0a]"
				)}>
					<Icon size={size === "large" ? 22 : 16} weight="fill" />
				</span>
			)}
			<span className={cn(
				"font-sans font-normal transition-colors duration-100",
				size === "large" ? "text-base" : "text-[14px]",
				disabled ? "text-white/40" : variant === "gold" ? "text-gold" : variant === "solid" ? "text-[#0a0a0a]" : "text-white group-hover:text-[#0a0a0a]"
			)}>
				{children}
			</span>
		</motion.button>
	);
}

// Comparison table component with dotted separators and descriptions
function ComparisonTable() {
	const rows = [
		{
			aspect: "Data",
			traditional: "Their servers",
			traditionalDesc: "Your files live on computers you don't own, run by a company that might not be around\u00A0forever.",
			basilica: "Your devices",
			basilicaDesc: "Replicates directly between your machines via encrypted peer-to-peer\u00A0sync.",
		},
		{
			aspect: "Trust",
			traditional: "Company policies",
			traditionalDesc: "You're trusting a company to keep its promises, but the fine print can change at any\u00A0time.",
			basilica: "Trustless",
			basilicaDesc: "Every operation is cryptographically signed \u2014 the mathematics itself is the\u00A0guarantee.",
		},
		{
			aspect: "Access",
			traditional: "Subscription",
			traditionalDesc: "You pay every month to use your own work, and lose access the moment you\u00A0stop.",
			basilica: "Perpetual",
			basilicaDesc: "Runs natively on your hardware with nothing to renew, expire, or call home\u00A0to.",
		},
		{
			aspect: "Ownership",
			traditional: "Licensed to you",
			traditionalDesc: "You don't own the software \u2014 you have permission to use it, which can be\u00A0revoked.",
			basilica: "Owned by you",
			basilicaDesc: "The full source is yours to audit, modify, and deploy entirely on your\u00A0terms.",
		},
	];

	return (
		<div className="w-full">
			{/* Header row - desktop only */}
			<div className="hidden md:grid grid-cols-[140px_1fr_1fr] gap-8 pb-4">
				<div className="text-base text-white/30 font-medium" />
				<div className="text-base text-white/30 font-medium">Traditional</div>
				<div className="text-base text-gold font-medium">Basilica</div>
			</div>
			<div className="hidden md:block"><DottedDivider /></div>

			{/* Data rows */}
			{rows.map((row, index) => (
				<motion.div key={row.aspect} initial={{ opacity: 0, y: 10 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-50px" }} transition={{ duration: 0.4, delay: index * 0.08 }}>
					{/* Desktop layout */}
					<div className="hidden md:grid grid-cols-[140px_1fr_1fr] gap-8 py-6">
						<div className="text-base text-white/30 font-medium">{row.aspect}</div>
						<div className="max-w-[360px]">
							<div className="text-base text-white/40 font-medium">{row.traditional}</div>
							<p className="mt-1.5 text-[16px] text-white/30 leading-relaxed">{row.traditionalDesc}</p>
						</div>
						<div className="max-w-[360px]">
							<div className="text-base text-white font-medium">{row.basilica}</div>
							<p className="mt-1.5 text-[16px] text-white/50 leading-relaxed">{row.basilicaDesc}</p>
						</div>
					</div>
					{/* Mobile layout - stacked */}
					<div className="md:hidden py-6 space-y-4">
						<div className="text-[19px] text-white font-medium tracking-tight">{row.aspect}</div>
						<div>
							<div className="text-[17px] text-white/50 font-medium">{row.traditional}</div>
							<p className="mt-1.5 text-[16px] text-white/30 leading-relaxed">{row.traditionalDesc}</p>
						</div>
						<div>
							<div className="text-[17px] text-white font-medium">{row.basilica}</div>
							<p className="mt-1.5 text-[16px] text-white/50 leading-relaxed">{row.basilicaDesc}</p>
						</div>
					</div>
					{index < rows.length - 1 && <DottedDivider />}
				</motion.div>
			))}
		</div>
	);
}

// Pillar card component
function PillarCard({
	icon: Icon,
	title,
	description,
	stat,
	statLabel,
	href,
	delay = 0,
}: {
	icon: React.ComponentType<{ size?: number; strokeWidth?: number }>;
	title: string;
	description: string;
	stat: string;
	statLabel: string;
	href: string;
	delay?: number;
}) {
	return (
		<motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay, ease: [0.25, 1, 0.5, 1] }} className="group relative p-6 bg-gradient-to-b from-white/[0.03] to-white/[0.01] border border-white/[0.06] rounded-xl hover:border-white/10 transition-colors duration-300">

			{/* Architectural top line accent */}
			<div className="absolute top-0 left-6 right-6 h-px bg-gradient-to-r from-transparent via-white/10 to-transparent" />

			{/* Header: Icon left, Stat right */}
			<div className="flex items-start justify-between">
				<div className="p-2.5 rounded-xl border-2 border-gold/30 text-gold">
					<Icon size={24} strokeWidth={1.5} />
				</div>
				<div className="text-right">
					<div className="text-[22px] font-medium tracking-tight text-white/90">{stat}</div>
					<div className="text-[12px] text-white/30 tracking-wide">{statLabel}</div>
				</div>
			</div>

			{/* Divider line */}
			<div className="my-5 h-px bg-white/[0.04]" />

			{/* Title and description */}
			<div className="space-y-2.5">
				<h3 className="text-[22px] font-medium tracking-tight text-white">{title}</h3>
				<p className="text-[16px] text-white/40 leading-[1.65]">{description}</p>
			</div>

			{/* Learn more link */}
			<a
				href={href}
				className="mt-5 inline-flex items-center gap-1.5 text-[16px] text-white/40 hover:text-gold hover:underline transition-colors duration-200"
			>
				Learn more
				<svg width="12" height="12" viewBox="0 0 12 12" fill="none" className="transition-transform duration-200 group-hover:translate-x-0.5">
					<path d="M4.5 2.5L8 6L4.5 9.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
				</svg>
			</a>
		</motion.div>
	);
}

// Slot-machine style digit that animates when value changes
function SlotDigit({ digit, delay = 0 }: { digit: string; delay?: number }) {
	return (
		<span className="inline-block overflow-hidden h-[1.15em] relative" style={{ width: digit === "," ? "0.35em" : "0.62em" }}>
			<AnimatePresence mode="popLayout">
				<motion.span
					key={digit}
					initial={{ y: "100%", opacity: 0 }}
					animate={{ y: "0%", opacity: 1 }}
					exit={{ y: "-100%", opacity: 0 }}
					transition={{
						duration: 0.3,
						delay,
						ease: [0.25, 1, 0.5, 1],
					}}
					className="absolute inset-0 flex items-center justify-center"
				>
					{digit}
				</motion.span>
			</AnimatePresence>
		</span>
	);
}

// Stacked round avatars — unique seeds, tighter overlap
const CREATOR_AVATARS = [
	"https://i.pravatar.cc/80?u=ada-lovelace",
	"https://i.pravatar.cc/80?u=linus-torvalds",
	"https://i.pravatar.cc/80?u=grace-hopper",
	"https://i.pravatar.cc/80?u=djikstra",
	"https://i.pravatar.cc/80?u=margaret-hamilton",
];

const SUSTAINER_AVATARS = [
	"https://i.pravatar.cc/80?u=satoshi-nakamoto",
	"https://i.pravatar.cc/80?u=vitalik-buterin",
	"https://i.pravatar.cc/80?u=balaji-srinivasan",
	"https://i.pravatar.cc/80?u=juan-benet",
	"https://i.pravatar.cc/80?u=moxie-marlinspike",
];

function AvatarStack({ avatars }: { avatars: string[] }) {
	return (
		<div className="flex">
			{avatars.map((src, i) => (
				<img
					key={src}
					src={src}
					alt=""
					className="w-8 h-8 rounded-full border-2 border-[#0f0f0f] object-cover"
					style={{ zIndex: avatars.length - i, marginLeft: i === 0 ? 0 : -6 - i * 2 }}
					loading="lazy"
				/>
			))}
		</div>
	);
}

// Stacked rounded-square app thumbnails — tighter overlap
const APP_THUMBNAILS = [
	"https://images.unsplash.com/photo-1620641788421-7a1c342ea42e?w=80&h=80&fit=crop",
	"https://images.unsplash.com/photo-1558591710-4b4a1ae0f04d?w=80&h=80&fit=crop",
	"https://images.unsplash.com/photo-1618005182384-a83a8bd57fbe?w=80&h=80&fit=crop",
	"https://images.unsplash.com/photo-1557672172-298e090bd0f1?w=80&h=80&fit=crop",
	"https://images.unsplash.com/photo-1614851099511-773084f6911d?w=80&h=80&fit=crop",
];

function AppThumbnailStack() {
	return (
		<div className="flex">
			{APP_THUMBNAILS.map((src, i) => (
				<img
					key={src}
					src={src}
					alt=""
					className="w-8 h-8 rounded-[7px] border-2 border-[#0f0f0f] object-cover"
					style={{ zIndex: APP_THUMBNAILS.length - i, marginLeft: i === 0 ? 0 : -6 - i * 2 }}
					loading="lazy"
				/>
			))}
		</div>
	);
}

// Single stat column: stack + number + label
function StatColumn({
	value,
	label,
	children,
}: {
	value: number;
	label: string;
	children: React.ReactNode;
}) {
	const formatted = value.toLocaleString();
	const digits = formatted.split("");

	return (
		<div className="flex flex-col-reverse md:flex-row items-center gap-2 md:gap-3">
			{children}
			<div className="text-center md:text-left">
				<div className="text-[19px] font-medium tracking-tight text-white tabular-nums font-mono flex justify-center md:justify-start leading-none">
					{digits.map((d, i) => (
						<SlotDigit key={`${i}-pos`} digit={d} delay={i * 0.03} />
					))}
				</div>
				<span className="text-[13px] text-white/35 block tracking-wide">{label}</span>
			</div>
		</div>
	);
}

// Live community counter — creators fastest, sustainers medium, apps slowest
function CommunityCounter() {
	const [creators, setCreators] = useState(2_847);
	const [sustainers, setSustainers] = useState(867);
	const [apps, setApps] = useState(419);

	// Creators: avg ~1.7s
	useEffect(() => {
		const tick = () => {
			const delay = 1000 + Math.random() * 1400;
			return setTimeout(() => {
				setCreators((c) => c + 1);
				timeoutRef = tick();
			}, delay);
		};
		let timeoutRef = tick();
		return () => clearTimeout(timeoutRef);
	}, []);

	// Sustainers: avg ~5s
	useEffect(() => {
		const tick = () => {
			const delay = 3000 + Math.random() * 4000;
			return setTimeout(() => {
				setSustainers((s) => s + 1);
				timeoutRef = tick();
			}, delay);
		};
		let timeoutRef = tick();
		return () => clearTimeout(timeoutRef);
	}, []);

	// Apps: avg ~14s
	useEffect(() => {
		const tick = () => {
			const delay = 9000 + Math.random() * 10000;
			return setTimeout(() => {
				setApps((a) => a + 1);
				timeoutRef = tick();
			}, delay);
		};
		let timeoutRef = tick();
		return () => clearTimeout(timeoutRef);
	}, []);

	return (
		<>
			{/* Mobile: 2-up top row + centered bottom */}
			<div className="md:hidden flex flex-col items-center gap-6 py-4">
				<div className="flex items-center gap-8">
					<StatColumn value={creators} label="Creators">
						<AvatarStack avatars={CREATOR_AVATARS} />
					</StatColumn>
					<div className="h-8 w-px bg-white/[0.06]" />
					<StatColumn value={sustainers} label="Sustainers">
						<AvatarStack avatars={SUSTAINER_AVATARS} />
					</StatColumn>
				</div>
				<StatColumn value={apps} label="Apps">
					<AppThumbnailStack />
				</StatColumn>
			</div>
			{/* Desktop: single row */}
			<div className="hidden md:inline-flex items-center gap-7 px-6 py-4">
				<StatColumn value={creators} label="Creators">
					<AvatarStack avatars={CREATOR_AVATARS} />
				</StatColumn>
				<div className="h-8 w-px bg-white/[0.06]" />
				<StatColumn value={sustainers} label="Sustainers">
					<AvatarStack avatars={SUSTAINER_AVATARS} />
				</StatColumn>
				<div className="h-8 w-px bg-white/[0.06]" />
				<StatColumn value={apps} label="Apps">
					<AppThumbnailStack />
				</StatColumn>
			</div>
		</>
	);
}

// Showcase app data
interface ShowcaseApp {
	name: string;
	category: string;
	description: string;
	longDescription: string;
	image: string;
	creator: string;
	avatar: string;
	likes: number;
	remixes: number;
}

const SHOWCASE_APPS: ShowcaseApp[] = [
	{
		name: "Persistent Game Worlds",
		category: "Gaming",
		description: "Multiplayer worlds that run across player devices. No central server to maintain or shut down.",
		longDescription: "Game worlds that outlive their creators. State replicates peer-to-peer across every player\u2019s device\u2014no central server to maintain, no monthly bills, no sunset date. If even one player cares, the world keeps running.",
		image: "https://images.unsplash.com/photo-1620641788421-7a1c342ea42e?w=600&h=340&fit=crop",
		creator: "maxis.dev",
		avatar: "https://i.pravatar.cc/150?u=maxis",
		likes: 847,
		remixes: 23,
	},
	{
		name: "Sample Libraries",
		category: "Audio",
		description: "Share sounds with automatic attribution. When someone uses your sample, you're credited.",
		longDescription: "Every sample carries its history. Use a loop in a track and the original creator is automatically credited\u2014cryptographically, not on the honor system. Attribution that travels with the audio, not a license PDF nobody reads.",
		image: "https://images.unsplash.com/photo-1558591710-4b4a1ae0f04d?w=600&h=340&fit=crop",
		creator: "loophaus",
		avatar: "https://i.pravatar.cc/150?u=loophaus",
		likes: 2341,
		remixes: 156,
	},
	{
		name: "Design Systems",
		category: "Components",
		description: "Component libraries that track usage. See where your work ends up.",
		longDescription: "Ship a component library and know exactly where it ends up. Every import, every fork, every adoption of your design tokens is tracked on-network. Open source that finally gives maintainers visibility into their impact.",
		image: "https://images.unsplash.com/photo-1618005182384-a83a8bd57fbe?w=600&h=340&fit=crop",
		creator: "una.ui",
		avatar: "https://i.pravatar.cc/150?u=una",
		likes: 1129,
		remixes: 89,
	},
	{
		name: "Collaborative Wikis",
		category: "Knowledge",
		description: "Shared knowledge bases where every contribution is tracked and attributed.",
		longDescription: "Wikis that can\u2019t be taken down, paywalled, or quietly rewritten. Every edit is signed by its author. Every contributor is credited permanently. Knowledge that belongs to the people who build it, hosted by the network itself.",
		image: "https://images.unsplash.com/photo-1557672172-298e090bd0f1?w=600&h=340&fit=crop",
		creator: "archivists",
		avatar: "https://i.pravatar.cc/150?u=archivists",
		likes: 562,
		remixes: 34,
	},
	{
		name: "Mod Ecosystems",
		category: "Gaming",
		description: "Game modifications with clear provenance. Modders keep control of their work.",
		longDescription: "Mods with verifiable authorship that no platform can delist. Derivatives automatically credit the original. Modders keep full control of distribution, updates, and licensing\u2014without depending on a storefront\u2019s good graces.",
		image: "https://images.unsplash.com/photo-1549317661-bd32c8ce0db2?w=600&h=340&fit=crop",
		creator: "nexus_",
		avatar: "https://i.pravatar.cc/150?u=nexus",
		likes: 3891,
		remixes: 412,
	},
	{
		name: "Research Datasets",
		category: "Science",
		description: "Open data with proper citation trails. Contributions to research properly tracked.",
		longDescription: "Datasets with immutable citation trails. Every contribution\u2014cleaning, annotating, extending\u2014is permanently recorded on-network. Data can never be silently altered or retracted. The unsexy infrastructure science has needed for decades.",
		image: "https://images.unsplash.com/photo-1614851099511-773084f6911d?w=600&h=340&fit=crop",
		creator: "openscience",
		avatar: "https://i.pravatar.cc/150?u=openscience",
		likes: 724,
		remixes: 67,
	},
];

// App download modal
function AppModal({ app, onClose }: { app: ShowcaseApp; onClose: () => void }) {
	// Close on Escape key
	useEffect(() => {
		const handleKey = (e: KeyboardEvent) => {
			if (e.key === "Escape") onClose();
		};
		window.addEventListener("keydown", handleKey);
		return () => window.removeEventListener("keydown", handleKey);
	}, [onClose]);

	return (
		<motion.div
			initial={{ opacity: 0 }}
			animate={{ opacity: 1 }}
			exit={{ opacity: 0 }}
			transition={{ duration: 0.15 }}
			className="fixed inset-0 z-50 flex items-center justify-center px-6"
			onClick={onClose}
		>
			{/* Backdrop */}
			<div className="absolute inset-0 bg-black/80 backdrop-blur-md" />

			{/* Modal */}
			<motion.div
				initial={{ opacity: 0, scale: 0.97, y: 8 }}
				animate={{ opacity: 1, scale: 1, y: 0 }}
				exit={{ opacity: 0, scale: 0.97, y: 8 }}
				transition={{ duration: 0.2, ease: [0.16, 1, 0.3, 1] }}
				onClick={(e) => e.stopPropagation()}
				className="relative w-full max-w-[460px] rounded-2xl bg-[#0e0e0e] border border-white/[0.08] overflow-hidden shadow-[0_32px_80px_-12px_rgba(0,0,0,0.8)]"
			>
				{/* Close button */}
				<button
					onClick={onClose}
					className="absolute top-3 right-3 z-10 p-1.5 rounded-full bg-black/50 backdrop-blur-sm text-white/70 hover:text-white hover:bg-black/70 transition-all duration-100"
				>
					<X size={14} weight="bold" />
				</button>

				{/* App image - clean cut, no gradient */}
				<div className="relative aspect-[2.4/1] overflow-hidden">
					<img
						src={app.image}
						alt={app.name}
						className="w-full h-full object-cover"
					/>
				</div>

				{/* Content */}
				<div className="px-5 pb-5 pt-4">
					<h3 className="text-[22px] font-medium tracking-tight text-white leading-tight">{app.name}</h3>

					{/* Creator + category + stats — single metadata row */}
					<div className="mt-2.5 flex items-center gap-3 text-[13px]">
						<div className="flex items-center gap-2 font-medium">
							<img src={app.avatar} alt={app.creator} className="w-5 h-5 rounded-full" />
							<span className="text-white/50">{app.creator}</span>
						</div>
						<span className="text-[11px] font-medium text-white/50 bg-white/[0.06] px-2.5 py-0.5 rounded-full">{app.category}</span>
						<span className="flex items-center gap-1 text-white/25 font-normal">
							<Heart size={12} weight="fill" />
							{app.likes.toLocaleString()}
						</span>
						<span className="flex items-center gap-1 text-white/25 font-normal">
							<ArrowsClockwise size={12} />
							{app.remixes}
						</span>
					</div>

					<p className="mt-4 text-[14px] text-white/40 leading-[1.65]">
						{app.longDescription}
					</p>

				</div>

				{/* Full-width divider */}
				<div className="border-t border-white/[0.06]" />

				{/* Download CTA */}
				<div className="px-5 py-4">
					<p className="text-[12px] text-white/25 mb-3 text-center">
						Download Basilica to run {app.name}
					</p>
					<div className="flex gap-2.5 justify-center">
						<Button icon={AppleLogo}>macOS</Button>
						<Button icon={WindowsLogo}>Windows</Button>
						<Button icon={LinuxLogo}>Linux</Button>
					</div>
				</div>
			</motion.div>
		</motion.div>
	);
}

export default function LandingPageRoute() {
	const scrollContainerRef = useRef<HTMLDivElement>(null);
	const headerHidden = useScrollDirection(scrollContainerRef);
	const globeContainerRef = useRef<HTMLDivElement>(null);
	const [isGlobeVisible, setIsGlobeVisible] = useState(true);
	const [selectedApp, setSelectedApp] = useState<ShowcaseApp | null>(null);
	const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

	// Globe always runs — keeps the animation feeling perpetual

	return (
		<div ref={scrollContainerRef} className="relative h-full w-full overflow-y-auto overflow-x-hidden bg-[#0a0a0a] text-white pt-5">

			{/* ===== HEADER ===== */}
			<motion.header
				initial={{ y: 0 }}
				animate={{ y: headerHidden ? "-150%" : 0 }}
				transition={{
					duration: 0.4,
					ease: [0.7, 0, 0.2, 1], // aggressive ease-out with fast start
				}}
				className="sticky top-0 z-40 px-4 md:px-[8%] lg:px-[15%] py-3"
			>
				<div className="relative bg-[#050505]/80 backdrop-blur-xl rounded-xl overflow-hidden">
					{/* Grain texture on header only */}
					<div
						className="pointer-events-none absolute inset-0 opacity-[0.08]"
						style={{
							backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 512 512' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='1.2' numOctaves='5' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")`,
						}}
					/>
					<nav className="relative flex items-center justify-between px-5 py-3">
					<motion.a
						href="/"
						initial={{ opacity: 0 }}
						animate={{ opacity: 1 }}
						transition={{ duration: 0.5 }}
						className="flex items-center"
					>
						<img src={logoLockup} alt="Basilica" className="h-[22px]" />
					</motion.a>

					{/* Desktop nav */}
					<motion.div
						initial={{ opacity: 0 }}
						animate={{ opacity: 1 }}
						transition={{ duration: 0.5, delay: 0.1 }}
						className="hidden md:flex items-center gap-10"
					>
						<a href="/about" className="text-[15px] text-white/60 hover:text-white transition-colors">
							About Us
						</a>
						<a href="/blog" className="text-[15px] text-white/60 hover:text-white transition-colors">
							Blog
						</a>
						<a href="/faqs" className="text-[15px] text-white/60 hover:text-white transition-colors">
							FAQs
						</a>
						<Button onClick={() => {
								const el = document.getElementById("get-access");
								const container = scrollContainerRef.current;
								if (!el || !container) return;
								const start = container.scrollTop;
								const end = el.offsetTop - container.offsetTop;
								const duration = 400;
								const t0 = performance.now();
								const step = (now: number) => {
									const p = Math.min((now - t0) / duration, 1);
									const ease = p < 0.5 ? 4 * p * p * p : 1 - Math.pow(-2 * p + 2, 3) / 2;
									container.scrollTop = start + (end - start) * ease;
									if (p < 1) requestAnimationFrame(step);
								};
								requestAnimationFrame(step);
							}}>
							Get Access
						</Button>
					</motion.div>
					{/* Mobile hamburger */}
					<motion.button
						initial={{ opacity: 0 }}
						animate={{ opacity: 1 }}
						transition={{ duration: 0.5, delay: 0.1 }}
						onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
						className="md:hidden relative w-8 h-8 flex items-center justify-center"
						aria-label="Toggle menu"
					>
						<motion.span
							animate={mobileMenuOpen ? { rotate: 45, y: 0 } : { rotate: 0, y: -3 }}
							transition={{ duration: 0.2 }}
							className="absolute w-5 h-[1.5px] bg-white/80 rounded-full"
						/>
						<motion.span
							animate={mobileMenuOpen ? { rotate: -45, y: 0 } : { rotate: 0, y: 3 }}
							transition={{ duration: 0.2 }}
							className="absolute w-5 h-[1.5px] bg-white/80 rounded-full"
						/>
					</motion.button>
					</nav>

					{/* Mobile menu */}
					<AnimatePresence>
						{mobileMenuOpen && (
							<motion.div
								initial={{ height: 0, opacity: 0 }}
								animate={{ height: "auto", opacity: 1 }}
								exit={{ height: 0, opacity: 0 }}
								transition={{ duration: 0.25, ease: [0.25, 1, 0.5, 1] }}
								className="md:hidden overflow-hidden"
							>
								<div className="px-5 pb-4 pt-1 flex flex-col">
									<a
										href="/about"
										onClick={() => setMobileMenuOpen(false)}
										className="flex items-center py-3 text-[15px] text-white/60 hover:text-white transition-colors"
									>
										About Us
									</a>
									<a
										href="/blog"
										onClick={() => setMobileMenuOpen(false)}
										className="flex items-center py-3 text-[15px] text-white/60 hover:text-white transition-colors"
									>
										Blog
									</a>
									<a
										href="/faqs"
										onClick={() => setMobileMenuOpen(false)}
										className="flex items-center py-3 text-[15px] text-white/60 hover:text-white transition-colors"
									>
										FAQs
									</a>
								</div>
							</motion.div>
						)}
					</AnimatePresence>
				</div>
			</motion.header>

			{/* ===== HERO ===== */}
			<section className="py-16 md:py-24 px-9 md:px-[8%] lg:px-[15%] overflow-hidden">
				<div className="flex flex-col md:flex-row items-center justify-center gap-8 md:gap-[clamp(64px,8vw,200px)]">
					{/* Left - Text content */}
					<div className="shrink-0 text-left">
						<motion.h1
							initial={{ opacity: 0, y: 20 }}
							animate={{ opacity: 1, y: 0 }}
							transition={{ duration: 0.6, delay: 0.2 }}
							className="font-sans text-[28px] md:text-[32px] lg:text-[42px] font-normal tracking-tight text-white"
						>
							Hello again, world.
						</motion.h1>

						{/* Subtitle */}
						<motion.p
							initial={{ opacity: 0, y: 15 }}
							animate={{ opacity: 1, y: 0 }}
							transition={{ duration: 0.5, delay: 0.35 }}
							className="mt-5 md:mt-6 max-w-lg font-sans text-[17px] md:text-xl leading-relaxed text-white/50"
						>
							Software that runs on networks, not corporations. Self-sustaining infrastructure for the next generation of applications.
						</motion.p>

						{/* Download Buttons */}
						<motion.div
							initial={{ opacity: 0, y: 20 }}
							animate={{ opacity: 1, y: 0 }}
							transition={{ duration: 0.5, delay: 0.5 }}
							className="mt-6 md:mt-8 flex gap-3"
						>
							<Button icon={AppleLogo}>macOS</Button>
							<Button icon={WindowsLogo}>Windows</Button>
							<Button icon={LinuxLogo}>Linux</Button>
						</motion.div>
					</div>

					{/* Right - 3D Earth Globe */}
					<motion.div
						ref={globeContainerRef}
						initial={{ opacity: 0, scale: 0.95 }}
						animate={{ opacity: 1, scale: 1 }}
						transition={{ duration: 1.2, delay: 0.2, ease: "easeOut" }}
						className="h-[300px] w-[300px] md:h-[380px] md:w-[380px] lg:h-[464px] lg:w-[464px] mx-auto md:mx-0 overflow-hidden flex-shrink-0"
					>
						<div className="origin-top-left scale-[0.6466] md:scale-[0.819] lg:scale-100 h-[464px] w-[464px]">
							<Suspense fallback={<GlobeSpinner />}>
								<Earth
									width={464}
									height={464}
									noBackground
									enablePointerInteraction={true}
									atmosphereAltitude={0.1}
									isPaused={false}
									initialLng={-140}
								/>
							</Suspense>
						</div>
					</motion.div>
				</div>
			</section>

			{/* ===== PRESS LOGOS ===== */}
			<section className="py-10 md:py-24 px-9 md:px-[8%] lg:px-[15%]">
				<motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} transition={{ duration: 0.6 }} className="grid grid-cols-3 md:flex md:items-center md:justify-center gap-y-7 gap-x-6 md:gap-16 justify-items-center max-w-xs mx-auto md:max-w-none">
					{[
						{ name: "The Block", src: "https://cdn.brandfetch.io/idKIPMiOhQ/theme/dark/logo.svg" },
						{ name: "Decrypt", src: "https://cdn.brandfetch.io/ido44e0GAr/w/400/h/302/theme/light/logo.png" },
						{ name: "CoinDesk", src: "https://cdn.brandfetch.io/idqY_V9808/theme/dark/logo.svg" },
						{ name: "Messari", src: "https://cdn.brandfetch.io/id4_c58dqo/theme/dark/logo.svg" },
						{ name: "Bankless", src: "https://cdn.brandfetch.io/idy-VbkbWC/theme/dark/logo.svg" },
						{ name: "Defiant", src: "https://cdn.brandfetch.io/idHp5VdAks/theme/dark/logo.svg" },
					].map((pub) => (
						<PressLogo key={pub.name} name={pub.name} src={pub.src} />
					))}
				</motion.div>
			</section>

			{/* ===== THE NEXT FRONTIER ===== */}
			<section className="relative py-16 md:py-24 px-9 md:px-[8%] lg:px-[15%]">
				<div className="mx-auto max-w-5xl">
					<motion.h2 initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: 0.1 }} className="text-left md:text-center text-[28px] md:text-[32px] lg:text-[42px] font-sans font-normal tracking-tight text-white leading-[1.2]">
						Self-sustaining software.
					</motion.h2>
					<motion.p initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: 0.2 }} className="mt-5 md:mt-6 text-left md:text-center text-[17px] md:text-xl text-white/50 leading-relaxed max-w-3xl md:mx-auto">
						Software that sustains itself because others depend on it. If it matters, it persists. If it doesn't, it fades. No gatekeepers deciding what survives—just a natural selection for&nbsp;software.
					</motion.p>

					{/* Showcase carousel */}
					<div className="mt-10 md:mt-14 -mx-9 px-9 md:-mx-[8%] md:px-[8%] lg:-mx-[15%] lg:px-[15%] overflow-hidden">
						<motion.div
							initial={{ opacity: 0 }}
							whileInView={{ opacity: 1 }}
							viewport={{ once: true }}
							transition={{ duration: 0.6, delay: 0.3 }}
							className="flex gap-4 overflow-x-auto overflow-y-hidden pb-4 scrollbar-none"
							style={{ scrollbarWidth: "none", overscrollBehaviorY: "none" }}
						>
							{SHOWCASE_APPS.map((app) => (
								<motion.div
									key={app.name}
									initial={{ opacity: 0, y: 20 }}
									whileInView={{ opacity: 1, y: 0 }}
									viewport={{ once: true }}
									transition={{ duration: 0.4 }}
									onClick={() => setSelectedApp(app)}
									className="group cursor-pointer flex-shrink-0 w-[240px] md:w-[280px] rounded-xl overflow-hidden bg-white/[0.03] border border-white/[0.06] hover:border-white/[0.12] transition-all duration-200"
								>
									{/* Thumbnail */}
									<div className="relative aspect-video overflow-hidden">
										<img
											src={app.image}
											alt={app.name}
											className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
											loading="lazy"
										/>
										<div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
										<span className="absolute bottom-2 left-2 text-[11px] font-medium text-white/70 bg-white/10 backdrop-blur-sm px-2 py-0.5 rounded-full">
											{app.category}
										</span>
									</div>
									{/* Content */}
									<div className="p-4">
										<h3 className="text-lg font-medium text-white">{app.name}</h3>
										<p className="mt-1.5 text-[15px] text-white/50 leading-relaxed line-clamp-2">
											{app.description}
										</p>
									</div>
									{/* Creator row */}
									<div className="px-4 pb-4 flex items-center justify-between">
										<div className="flex items-center gap-2">
											<img
												src={app.avatar}
												alt={app.creator}
												className="w-5 h-5 rounded-full"
											/>
											<span className="text-[14px] text-white/40">{app.creator}</span>
										</div>
										<div className="flex items-center gap-3 text-white/30">
											<span className="flex items-center gap-1 text-[13px]">
												<Heart size={13} weight="fill" />
												{app.likes.toLocaleString()}
											</span>
											<span className="flex items-center gap-1 text-[13px]">
												<ArrowsClockwise size={13} />
												{app.remixes}
											</span>
										</div>
									</div>
								</motion.div>
							))}
						</motion.div>
					</div>
				</div>
			</section>


			{/* ===== A SELF-SUSTAINING ALTERNATIVE ===== */}
			<section className="relative py-16 md:py-24 px-9 md:px-[8%] lg:px-[15%]">
				<div className="mx-auto max-w-5xl">
					<motion.h2 initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: 0.1 }} className="mt-4 text-left md:text-center text-[28px] md:text-[32px] lg:text-[42px] font-sans font-normal tracking-tight text-white">
						Networks, not corporations.
					</motion.h2>
					<motion.p initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: 0.2 }} className="mt-5 md:mt-6 text-left md:text-center text-[17px] md:text-xl text-white/50 max-w-3xl md:mx-auto leading-relaxed mb-10 md:mb-14">
						Corporations extract value at the center. Networks distribute it to the edges—to every contributor and dependency that makes grassroots software possible. Basilica is infrastructure for applications that run on participant networks and reward value throughout the supply&nbsp;chain.
					</motion.p>

					<ComparisonTable />
				</div>
			</section>

			{/* ===== FOUR PILLARS ===== */}
			<section className="relative py-16 md:py-24 px-9 md:px-[8%] lg:px-[15%]">
				<div className="mx-auto max-w-5xl">
					<motion.h2 initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: 0.1 }} className="mt-4 text-left md:text-center text-[28px] md:text-[32px] lg:text-[42px] font-sans font-normal tracking-tight text-white">
						Built from the ground up.
					</motion.h2>
					<motion.p initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: 0.2 }} className="mt-5 md:mt-6 text-left md:text-center text-[17px] md:text-xl text-white/50 max-w-3xl md:mx-auto leading-relaxed">
						Identity, ownership, and economics aren't bolted on—they're native to the network. The result is an internet where attribution is automatic and collaboration is&nbsp;permissionless.
					</motion.p>

					{/* Pillar cards grid */}
					<div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5 mt-10 md:mt-14 max-w-[820px] mx-auto">
						<PillarCard
							icon={Fingerprint}
							title="Self-defending"
							description="Signature aggregation and elastic validation with post-quantum cryptography. No central server, no single point of&nbsp;failure."
							stat="256-bit"
							statLabel="encryption"
							href="/docs/security"
							delay={0}
						/>
						<PillarCard
							icon={Boxes}
							title="Self-hosting"
							description="An integrated filesystem designed for exabytes of rich media content. Ten thousand times more efficient than current&nbsp;alternatives."
							stat="10,000×"
							statLabel="efficiency"
							href="/docs/storage"
							delay={0.1}
						/>
						<PillarCard
							icon={Network}
							title="Self-ordering"
							description="Novel proof of PageRank enables fully decentralized consensus. Quality surfaces through mathematics, not human&nbsp;curation."
							stat="50k"
							statLabel="tps per node"
							href="/docs/consensus"
							delay={0.2}
						/>
						<PillarCard
							icon={Gem}
							title="Self-funding"
							description="Block rewards distributed to all network participants. Creators and contributors earn real revenue starting from day&nbsp;one."
							stat="100%"
							statLabel="to creators"
							href="/docs/economics"
							delay={0.3}
						/>
					</div>
				</div>
			</section>

			{/* ===== BUILD WITH US ===== */}
			<section id="get-access" className="relative py-16 md:py-24 px-9 md:px-[8%] lg:px-[15%]">
				<div className="mx-auto max-w-3xl text-center">
					<motion.h2 initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: 0.1 }} className="text-[28px] md:text-[32px] lg:text-[42px] font-sans font-normal tracking-tight text-white">
						Build what matters.
					</motion.h2>

					<motion.p initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: 0.2 }} className="mt-5 md:mt-6 text-[17px] md:text-xl text-white/45 leading-relaxed">
						A new digital frontier for people who build things that are important to the public. Help us build a great foundation for a better&nbsp;internet.
					</motion.p>

					{/* Live counters */}
					<motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: 0.3 }} className="mt-10">
						<CommunityCounter />
					</motion.div>

					{/* Email signup */}
					<motion.form
						initial={{ opacity: 0, y: 20 }}
						whileInView={{ opacity: 1, y: 0 }}
						viewport={{ once: true }}
						transition={{ duration: 0.5, delay: 0.4 }}
						className="mt-8 md:mt-10 flex flex-col md:flex-row items-center gap-4 md:gap-3 max-w-md mx-auto"
						onSubmit={(e) => e.preventDefault()}
					>
						<input
							type="email"
							placeholder="enter your email"
							className="w-full md:flex-1 h-[48px] px-5 rounded-xl bg-white/5 text-white placeholder:text-white/30 text-base focus:outline-none focus:bg-white/[0.07] transition-all duration-200"
						/>
						<Button type="submit" disabled size="large">
							Get Access
						</Button>
					</motion.form>

				</div>
			</section>

			{/* ===== FOOTER ===== */}
			<footer className="relative mt-16 md:mt-24 pt-8 pb-12 md:pb-20 px-9 md:px-[8%] lg:px-[15%] border-t border-white/5">
				<div className="flex flex-col md:flex-row items-center justify-between gap-6">
					<div className="flex items-center gap-2">
						<BasilicaLogo size={16} className="text-white/40" />
						<span className="text-[15px] text-white/40">© 2025 Basilica Digital Inc.</span>
					</div>

					<div className="flex items-center gap-5">
						<a href="/privacy" className="text-[15px] text-white/40 hover:text-white transition-colors duration-150">
							Privacy
						</a>
						<a href="/terms" className="text-[15px] text-white/40 hover:text-white transition-colors duration-150">
							Terms
						</a>
						<a href="https://github.com/basilica" className="group text-white/40 hover:text-white transition-colors duration-150">
							<GithubLogo size={20} weight="regular" className="group-hover:hidden" />
							<GithubLogo size={20} weight="fill" className="hidden group-hover:block" />
						</a>
						<a href="https://discord.gg/basilica" className="group text-white/40 hover:text-white transition-colors duration-150">
							<DiscordLogo size={20} weight="regular" className="group-hover:hidden" />
							<DiscordLogo size={20} weight="fill" className="hidden group-hover:block" />
						</a>
						<a href="https://x.com/basilica" className="group text-white/40 hover:text-white transition-colors duration-150">
							<XLogo size={20} weight="regular" className="group-hover:hidden" />
							<XLogo size={20} weight="fill" className="hidden group-hover:block" />
						</a>
					</div>
				</div>
			</footer>

			{/* App download modal */}
			<AnimatePresence>
				{selectedApp && (
					<AppModal app={selectedApp} onClose={() => setSelectedApp(null)} />
				)}
			</AnimatePresence>
		</div>
	);
}
